"use client";

import { useWindowWidth } from "@commerce-frontend/ui/hooks/responsive";
import {
	getLocalStorageItem,
	setLocalStorageItem,
} from "@commerce-frontend/ui/utils/localStorage";
import { useDebouncedCallback } from "@commerce-frontend/ui/utils/useDebouncedCallback";
import type { Locale } from "@commerce-shared/site-config";
import type { FragmentOf } from "gql.tada";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import type { CardProductFragment } from "~/components/product/ProductCard/ProductCard.fragments";
import { useUserData } from "~/hooks/auth";
import { useSearchResult } from "~/hooks/search-results";
import type {
	CategoryPageResultFragment,
	ContentPageResultFragment,
	SearchResultFragment,
} from "~/lib/queries/graphql/fragments";
import { MobileSearchModal } from "../MobileSearchModal/MobileSearchModal";
import { SearchBar } from "../SearchBar/SearchBar";
import { FakeSearchBar } from "./FakeSearchBar";

type Props = {
	locale: Locale;
};

export type SearchResultEntry = {
	id: string;
	value: string;
};

export type SearchResultObject = {
	algoliaQueryId: string | null | undefined;
	badges: SearchResultEntry[];
	searchTerms: SearchResultEntry[];
	categories: FragmentOf<typeof CategoryPageResultFragment>[];
	pages: FragmentOf<typeof ContentPageResultFragment>[];
	products: FragmentOf<typeof CardProductFragment>[];
};

const localStorageKey = "recent-search-terms";
const maxRecentSearchTerms = 5;

export function SearchBarGroup({ locale }: Props) {
	const { windowWidth } = useWindowWidth();
	const isMobile = windowWidth.upToMobile;

	const [isClient, setIsClient] = useState(false);

	const searchParams = useSearchParams();
	const [value, setValue] = useState("");
	const [isSearchParamsReady, setIsSearchParamsReady] = useState(false);

	useEffect(() => {
		if (!isSearchParamsReady) {
			setValue(searchParams.get("q") ?? "");
			setIsSearchParamsReady(true);
		}
	}, [searchParams, isSearchParamsReady]);

	const userData = useUserData();
	const customerGroupKey = userData?.group;

	// This state is used to detect if the value is changed by typing in the input field
	const [valueChangedByInput, setValueChangedByInput] = useState(false);

	// This state is used to detect if the search results are being loaded or not
	const [loadingSearchResults, setLoadingSearchResults] =
		useState<boolean>(false);

	// This state is used to show/hide the quick search (desktop only)
	const [showQuickSearch, setShowQuickSearch] = useState(false);

	// This state is used to show/hide the mobile search modal (mobile only)
	const [mobileSearchModalIsOpen, setMobileSearchModalIsOpen] = useState(false);

	// This state is used to store the recent search terms
	const [recentSearchTerms, setRecentSearchTerms] = useState<string[]>([]);

	useEffect(() => {
		setIsClient(true);
		setRecentSearchTerms(getLocalStorageItem<string[]>(localStorageKey) ?? []);
	}, []);

	// Initial fetch of search result (includes refetch function)
	const { searchResult, refetch } = useSearchResult(
		value.length > 3 ? value : "",
		customerGroupKey,
	);

	// Debounced fetch of search result
	const getSearchResultDebounced = useDebouncedCallback(
		async (search: string) => {
			if (search.length > 3) {
				await refetch();
			}
			setLoadingSearchResults(false);
		},
		250,
	);

	// Immediate fetch of search result
	const getSearchResult = useDebouncedCallback(async () => {
		await refetch();
		setLoadingSearchResults(false);
	}, 0);

	// Fetch search suggestions when the modal is opened or the search term is changed
	useEffect(() => {
		if (!valueChangedByInput && (showQuickSearch || mobileSearchModalIsOpen)) {
			getSearchResult(value);
		}
		if (typeof window !== "undefined") {
			const dataLayer = window.dataLayer || [];
			dataLayer.push({ event: "Hits Viewed" });
		}
	}, [value, showQuickSearch, mobileSearchModalIsOpen]);

	// General function to modify the recent search terms in the local storage and state
	const changeRecentSearchTerms = (value: string[]) => {
		const limitedValue = value.slice(0, maxRecentSearchTerms);
		setLocalStorageItem(localStorageKey, limitedValue);
		setRecentSearchTerms(limitedValue);
	};

	// Function to add a recent search term
	const addRecentSearchTerm = (value: string) => {
		const newRecentSearchTerms = [
			value,
			...recentSearchTerms.filter((term: string) => term !== value),
		];
		changeRecentSearchTerms(newRecentSearchTerms);
	};

	// Function to delete a recent search term
	const deleteRecentSearchTerm = (value: string) => {
		const newRecentSearchTerms = recentSearchTerms.filter(
			(term: string) => term !== value,
		);
		changeRecentSearchTerms(newRecentSearchTerms);
	};

	// Function to handle the input value change (differentiates between typing and for example selecting a recent search term or clicking the close button)
	const onInputValueChange = (search: string) => {
		setValueChangedByInput(true);
		setValue(search);
		setLoadingSearchResults(true);
		getSearchResultDebounced(search);
		setTimeout(() => {
			setValueChangedByInput(false);
		}, 200);
	};

	// Function to handle the selection of a link (badge, recent search term, search term)
	const onSelectLink = (
		linkValue: string,
		shouldAddToRecentSearchTerms: boolean,
	) => {
		setValue(linkValue);
		closeQuickSearch();
		setMobileSearchModalIsOpen(false);
		if (shouldAddToRecentSearchTerms) {
			addRecentSearchTerm(linkValue);
		}
	};

	// Function to handle the selection of a product link
	const onSelectProductLink = () => {
		setValue("");
		closeQuickSearch();
		setMobileSearchModalIsOpen(false);
	};

	// Function to handle the deletion of a recent search term
	const onClickDeleteLink = (linkValue: string) =>
		deleteRecentSearchTerm(linkValue);

	// Function to handle the submit link (close the quick search and the mobile search modal)
	const onClickSubmitLink = (value: string) => {
		closeQuickSearch();
		setMobileSearchModalIsOpen(false);
		addRecentSearchTerm(value);
	};

	// Function to handle the close button click
	const onClickCloseButton = () => {
		setValue("");
		if (!isMobile) {
			openQuickSearch();
		}
	};

	// Function to handle the focus of the input field
	const onFocusInput = () => {
		if (!isMobile) {
			openQuickSearch();
		}
	};

	// Function to handle the blur of the input field
	const onBlurInput = () => {
		if (!isMobile) {
			closeQuickSearch();
		}
	};

	// Function to handle the click on the fake search bar (mobile only)
	const onClickFakeSearchBar = () => {
		setMobileSearchModalIsOpen(true);
	};

	const closeQuickSearch = () => {
		setShowQuickSearch(false);
		document.querySelector<HTMLInputElement>("input[name=q]")?.blur();
	};

	const openQuickSearch = () => {
		setShowQuickSearch(true);
		document.querySelector<HTMLInputElement>("input[name=q]")?.focus();
		window.requestAnimationFrame(() => {
			// Find search no result in DOM
			const searchNoResult = document.querySelector(
				'[cmdk-item][data-search-no-result="true"]',
			);

			// Check if search no result exists
			if (searchNoResult) {
				// Remove all previously selected attributes
				const elementsWithAttributes = document.querySelectorAll(
					'[cmdk-item][data-selected="true"]',
				);
				if (elementsWithAttributes.length > 0) {
					elementsWithAttributes[0].removeAttribute("data-selected");
				}

				// Add selected attributes to search no result
				searchNoResult?.setAttribute("data-selected", "true");
				searchNoResult?.setAttribute("aria-selected", "true");
			}
		});
	};

	return (
		<>
			<SearchBar
				locale={locale}
				className="max-sm:hidden"
				value={value}
				showQuickSearch={showQuickSearch}
				recentSearchTerms={value.length > 3 ? [] : recentSearchTerms}
				searchResult={toSearchResultObject(value, searchResult)}
				loadingSearchResults={loadingSearchResults}
				onInputValueChange={onInputValueChange}
				onSelectLink={onSelectLink}
				onSelectProductLink={onSelectProductLink}
				onClickDeleteLink={onClickDeleteLink}
				onClickSubmitLink={onClickSubmitLink}
				onClickCloseButton={onClickCloseButton}
				onFocusInput={onFocusInput}
				onBlurInput={onBlurInput}
			/>
			<FakeSearchBar
				className="sm:hidden"
				onClickFakeSearchBar={onClickFakeSearchBar}
			/>
			<MobileSearchModal
				locale={locale}
				value={value}
				showQuickSearch={showQuickSearch}
				recentSearchTerms={value.length > 3 ? [] : recentSearchTerms}
				searchResult={toSearchResultObject(value, searchResult)}
				loadingSearchResults={loadingSearchResults}
				open={mobileSearchModalIsOpen}
				onOpenChange={setMobileSearchModalIsOpen}
				onInputValueChange={onInputValueChange}
				onSelectLink={onSelectLink}
				onSelectProductLink={onSelectProductLink}
				onClickDeleteLink={onClickDeleteLink}
				onClickSubmitLink={onClickSubmitLink}
				onClickCloseButton={onClickCloseButton}
				onFocusInput={onFocusInput}
				onBlurInput={onBlurInput}
			/>
		</>
	);
}

const toSearchResultObject = (
	value: string,
	result?: FragmentOf<typeof SearchResultFragment> | null,
): SearchResultObject => ({
	algoliaQueryId: result?.algoliaQueryId,
	badges: value.length > 3 ? [] : (result?.suggestions ?? []),
	searchTerms: value.length > 3 ? (result?.suggestions ?? []) : [],
	categories: value.length > 3 ? (result?.categories ?? []) : [],
	pages: value.length > 3 ? (result?.pages ?? []) : [],
	products: value.length > 3 ? (result?.products ?? []) : [],
});
